/**
 * Secure cookie utility for Strykr.ai
 * Provides methods for safely storing and retrieving authentication data
 * using HttpOnly cookies where possible, with secure fallbacks
 */

import { logger } from './logger';

// Platform detection
const isDesktop = typeof window !== 'undefined' && '__TAURI__' in window;

// Cookie options for secure storage
interface CookieOptions {
  maxAge?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

// Default cookie settings for browser compatibility
const defaultOptions: CookieOptions = {
  maxAge: 60 * 60 * 24 * 7, // 7 days
  path: '/',
  secure: isDesktop ? false : window.location.protocol === 'https:', // Desktop doesn't need HTTPS
  sameSite: isDesktop ? 'lax' : 'none', // Desktop can use 'lax', web needs 'none' for third-party cookies
  // Configure cookie domain to work with custom auth domain
  // Note: Using exact domain matches rather than wildcards for better compatibility
  domain: determineOptimalDomain()
};

// Determine the optimal domain setting based on environment and browser capabilities
function determineOptimalDomain(): string | undefined {
  // In desktop applications, don't set domain restrictions
  if (isDesktop) {
    return undefined;
  }
  
  // In production, we need to handle various subdomain scenarios
  if (import.meta.env.MODE === 'production') {
    const hostname = window.location.hostname;
    
    // If we're already on the exact domain that matches the cookie, use that
    if (hostname === 'app.strykr.ai' || hostname === 'auth.strykr.ai') {
      return hostname; // Use exact domain match for better compatibility
    }
    
    // Default fallback to parent domain with wildcard
    return '.strykr.ai';
  }
  
  // For local development, use no domain specification
  return undefined;
}

/**
 * Set a cookie with the specified options
 */
const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const opts = { ...defaultOptions, ...options };
  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;
  
  if (opts.maxAge) {
    cookieString = `${cookieString}; Max-Age=${opts.maxAge}`;
  }
  if (opts.domain) {
    cookieString = `${cookieString}; Domain=${opts.domain}`;
  }
  if (opts.path) {
    cookieString = `${cookieString}; Path=${opts.path}`;
  }
  if (opts.secure) {
    cookieString = `${cookieString}; Secure`;
  }
  if (opts.sameSite) {
    cookieString = `${cookieString}; SameSite=${opts.sameSite}`;
  }
  
  document.cookie = cookieString;
  logger.debug(`Cookie set: ${name} (HttpOnly: false)`);
};

/**
 * Get a cookie by name
 */
const getCookie = (name: string): string | null => {
  const nameString = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(nameString) === 0) {
      return decodeURIComponent(cookie.substring(nameString.length, cookie.length));
    }
  }
  
  return null;
};

/**
 * Delete a cookie by setting its expiration in the past
 */
const deleteCookie = (name: string, options: CookieOptions = {}): void => {
  const opts = { ...options, maxAge: -1 };
  setCookie(name, '', opts);
  logger.debug(`Cookie deleted: ${name}`);
};

/**
 * Storage adapter compatible with Supabase Auth
 * Uses cookies instead of localStorage with memory caching for performance
 */

// Simple cache for frequently accessed values
interface CacheItem {
  value: string;
  expiry: number;
}

const memoryCache: Record<string, CacheItem> = {};
const CACHE_TTL = 10000; // 10 seconds cache time

export const cookieStorage = {
  getItem: (key: string): string | null => {
    try {
      // Check if we have a non-expired cached value
      const now = Date.now();
      const cached = memoryCache[key];
      if (cached && cached.expiry > now) {
        return cached.value;
      }
      
      // If not in cache or expired, get from cookie
      const value = getCookie(key);
      
      // Only log retrieval when actually accessing the cookie, not the cache
      logger.debug(`Retrieved from secure storage: ${key}`);
      
      // If value is null, try sessionStorage fallback for third-party cookie restrictions
      if (!value && window.sessionStorage) {
        const sessionValue = window.sessionStorage.getItem(`fallback_${key}`);
        if (sessionValue) {
          logger.debug(`Retrieved from session fallback: ${key}`);
          
          // Cache the result from fallback too
          memoryCache[key] = {
            value: sessionValue,
            expiry: now + CACHE_TTL
          };
          
          return sessionValue;
        }
      }
      
      // Cache the result if it exists
      if (value) {
        memoryCache[key] = {
          value,
          expiry: now + CACHE_TTL
        };
      }
      
      return value;
    } catch (error) {
      logger.error(`Error getting from secure storage: ${key}`, error);
      return null;
    }
  },
  
  setItem: (key: string, value: string): void => {
    try {
      // Update cookie
      setCookie(key, value);
      
      // Update the cache
      memoryCache[key] = {
        value,
        expiry: Date.now() + CACHE_TTL
      };
      
      // Also store in sessionStorage as fallback for third-party cookie restrictions
      if (window.sessionStorage) {
        window.sessionStorage.setItem(`fallback_${key}`, value);
        logger.debug(`Stored in session fallback: ${key}`);
      }
      
      logger.debug(`Stored in secure storage: ${key}`);
    } catch (error) {
      logger.error(`Error storing in secure storage: ${key}`, error);
    }
  },
  
  removeItem: (key: string): void => {
    try {
      // Remove from cookie
      deleteCookie(key);
      
      // Remove from cache
      delete memoryCache[key];
      
      // Also remove from sessionStorage fallback
      if (window.sessionStorage) {
        window.sessionStorage.removeItem(`fallback_${key}`);
        logger.debug(`Removed from session fallback: ${key}`);
      }
      
      logger.debug(`Removed from secure storage: ${key}`);
    } catch (error) {
      logger.error(`Error removing from secure storage: ${key}`, error);
    }
  }
};

/**
 * For storing temporary auth information like verification emails
 */
export const secureAuthStorage = {
  /**
   * Store verification email securely
   */
  storeVerificationEmail: (email: string): void => {
    try {
      setCookie('verification_email', email, { maxAge: 60 * 30 }); // 30 minutes
      logger.debug('Verification email stored securely');
    } catch (error) {
      logger.error('Error storing verification email', error);
    }
  },
  
  /**
   * Retrieve verification email
   */
  getVerificationEmail: (): string | null => {
    try {
      return getCookie('verification_email');
    } catch (error) {
      logger.error('Error retrieving verification email', error);
      return null;
    }
  },
  
  /**
   * Clear verification email
   */
  clearVerificationEmail: (): void => {
    try {
      deleteCookie('verification_email');
      logger.debug('Verification email cleared');
    } catch (error) {
      logger.error('Error clearing verification email', error);
    }
  }
};

export default {
  setCookie,
  getCookie,
  deleteCookie,
  cookieStorage,
  secureAuthStorage
};
